import React, { useState, useMemo, useEffect } from 'react';
import { X, ShieldAlert, Calculator, AlertCircle } from 'lucide-react';
import type { ScannerRecord, StopChoice } from '../types';
import { sizePosition4G, roundToTwseTick } from '../lib/sizer';

interface StockDrawerProps {
  stock: ScannerRecord | null;
  onClose: () => void;
  macroMultiplier: number;
}

export type EquityUnit = 'wan' | 'ntd';

export function formatWan(ntd: number): string {
  if (ntd <= 0) return '0 萬元';
  if (ntd >= 100_000_000) {
    const yi = ntd / 100_000_000;
    const wan = ntd / 10_000;
    return `${yi.toLocaleString()} 億元 (${wan.toLocaleString()} 萬元)`;
  }
  const wan = ntd / 10_000;
  if (Number.isInteger(wan)) {
    return `${wan.toLocaleString()} 萬元`;
  }
  return `${wan.toFixed(1)} 萬元`;
}

export function parseEquityInput(inputStr: string, unit: EquityUnit): number {
  const cleaned = inputStr.trim().replace(/,/g, '');
  if (!cleaned) return 0;

  // Check for explicit suffix like "80萬", "80w", "200萬", "200w", "1億"
  const yiMatch = cleaned.match(/^([0-9.]+)\s*(億)$/);
  if (yiMatch) {
    return Math.max(0, Math.round(parseFloat(yiMatch[1]) * 100_000_000));
  }
  const wanMatch = cleaned.match(/^([0-9.]+)\s*(萬|w|W)$/);
  if (wanMatch) {
    return Math.max(0, Math.round(parseFloat(wanMatch[1]) * 10_000));
  }

  const num = parseFloat(cleaned);
  if (isNaN(num) || num <= 0) return 0;

  if (unit === 'wan') {
    // In 萬元 mode, numbers like 80, 200, 1000, 10000 are in 萬元 (e.g. 10000 萬 = 1 億 NTD)
    // Only if a user pasted raw NTD with 7+ digits (>= 1,000,000, i.e. 10 billion in wan mode) do we treat as raw NTD
    if (num >= 1_000_000) {
      return Math.round(num);
    }
    return Math.round(num * 10_000);
  }

  return Math.round(num);
}

export const StockDrawer: React.FC<StockDrawerProps> = ({
  stock,
  onClose,
  macroMultiplier,
}) => {
  if (!stock) return null;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Unit and input state: default to 'wan' (萬元) with initial 200 (200萬 = 2,000,000 NTD)
  const [equityUnit, setEquityUnit] = useState<EquityUnit>('wan');
  const [equityInputStr, setEquityInputStr] = useState<string>('200');
  const [portfolioEquity, setPortfolioEquity] = useState<number>(2_000_000);

  // Stop choice state
  const [stopChoice, setStopChoice] = useState<StopChoice>('atr');
  const [manualStop, setManualStop] = useState<number>(() =>
    roundToTwseTick(stock.close * 0.95, 'floor')
  );
  const [manualStopInputStr, setManualStopInputStr] = useState<string>(() =>
    roundToTwseTick(stock.close * 0.95, 'floor').toString()
  );

  // Simulation mode toggle (allows user to simulate 1.0x sizing even for A3/B1/B2 stocks)
  const [simulationMode, setSimulationMode] = useState<boolean>(false);

  // Sync manual stop price whenever stock changes
  useEffect(() => {
    if (stock) {
      const defaultStop = roundToTwseTick(stock.close * 0.95, 'floor');
      setManualStop(defaultStop);
      setManualStopInputStr(defaultStop.toString());
      setSimulationMode(false);
    }
  }, [stock?.symbol, stock?.close]);

  const handleUnitSwitch = (newUnit: EquityUnit) => {
    if (newUnit === equityUnit) return;
    setEquityUnit(newUnit);
    if (newUnit === 'wan') {
      const wanValue = portfolioEquity / 10_000;
      setEquityInputStr(Number.isInteger(wanValue) ? wanValue.toString() : wanValue.toFixed(2));
    } else {
      setEquityInputStr(portfolioEquity.toString());
    }
  };

  const handleEquityChange = (val: string) => {
    setEquityInputStr(val);
    const parsed = parseEquityInput(val, equityUnit);
    setPortfolioEquity(parsed);
  };

  const handlePresetClick = (valWan: number, valNtd: number) => {
    setPortfolioEquity(valNtd);
    if (equityUnit === 'wan') {
      setEquityInputStr(valWan.toString());
    } else {
      setEquityInputStr(valNtd.toString());
    }
  };

  const handleManualStopChange = (val: string) => {
    setManualStopInputStr(val);
    const num = parseFloat(val);
    if (!isNaN(num) && num > 0) {
      setManualStop(num);
    }
  };

  const disciplineMultiplier = stock.matrix_multiplier * macroMultiplier;
  const effectiveMultiplier = simulationMode ? 1.0 : disciplineMultiplier;

  const sizingResult = useMemo(() => {
    return sizePosition4G({
      portfolio_equity: portfolioEquity,
      entry_price: stock.close,
      stop_choice: stopChoice,
      atr14: stock.atr14 ?? stock.close * 0.03,
      base_low: stock.low_20d ?? stock.close * 0.92,
      stop_loss_price: stopChoice === 'manual' ? manualStop : undefined,
      macro_multiplier: effectiveMultiplier,
      max_equity_cap: 1.0,
    });
  }, [stock, portfolioEquity, stopChoice, manualStop, effectiveMultiplier]);

  const getConstraintBadge = (constraint: string) => {
    switch (constraint) {
      case 'WEIGHT':
        return {
          label: 'WEIGHT (單一標的 10% 資金上限)',
          desc: '受限於單一標的不得超過投資組合總淨值 10% 之分散紀律',
          color: 'text-amber-300 border-amber-500/40 bg-amber-950/40',
        };
      case 'RISK':
        return {
          label: 'RISK (1% 淨值停損風險上限)',
          desc: '受限於單筆交易觸及停損時，最大虧損不得超過總淨值 1%',
          color: 'text-rose-400 border-rose-500/40 bg-rose-950/40',
        };
      case 'CAP_HEADROOM':
        return {
          label: 'CAP_HEADROOM (總持股上限)',
          desc: '受限於投資組合最大許可總持股額度',
          color: 'text-indigo-400 border-indigo-500/40 bg-indigo-950/40',
        };
      case 'ZERO_MULTIPLIER':
        return {
          label: 'ZERO_MULTIPLIER (風控紀律乘數 0.0x)',
          desc: `處於 ${stock.phase} 階段，科斯托蘭尼風控紀律嚴格禁止買進建立新倉`,
          color: 'text-slate-400 border-slate-700 bg-slate-900/60',
        };
      default:
        return {
          label: constraint || 'NONE',
          desc: '無特殊拘束限制',
          color: 'text-slate-300 border-[#1e293b] bg-[#0b0f17]',
        };
    }
  };

  const constraintInfo = getConstraintBadge(sizingResult.constraint);

  return (
    <div data-testid="stock-drawer" className="fixed inset-0 z-50 overflow-hidden bg-black/70 backdrop-blur-sm flex justify-end">
      <div className="w-full max-w-lg bg-[#0e141f] border-l border-[#1e293b]/80 h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200">
        {/* Drawer Header */}
        <div className="p-4 flex items-center justify-between bg-[#0b0f17]">
          <div className="flex items-center space-x-3">
            <div>
              <div className="flex items-center space-x-2">
                <span className="text-base font-bold font-mono text-[#38bdf8]">{stock.symbol}</span>
                <span className="text-sm font-sans font-semibold text-slate-100">{stock.name}</span>
                <span className="text-xs px-2 py-0.5 rounded font-mono font-bold bg-blue-950/80 text-sky-300 border border-sky-500/40">
                  {stock.phase}
                </span>
              </div>
              <p className="text-xs text-slate-300 font-sans mt-0.5">
                <span className="text-sky-400/90 font-medium">{stock.sector}</span> · 共振訊號:{' '}
                <strong className="text-slate-100 font-mono">{stock.resonance_signal}</strong>
              </p>
            </div>
          </div>

          <button
            data-testid="drawer-close-btn"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#192231] text-slate-300 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Drawer Content Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Microstructure Safety Alert Bar */}
          {(stock.is_disposition || stock.is_limit_locked) && (
            <div className="p-3 rounded-xl bg-rose-950/50 border border-rose-500/40 flex items-start space-x-2.5">
              <ShieldAlert className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" />
              <div className="text-xs text-rose-200">
                <p className="font-bold">臺灣市場微結構警示：</p>
                {stock.is_disposition && <p>• 本標的目前處於「處置股」狀態，分盤撮合並預收款券，不建議建立新倉。</p>}
                {stock.is_limit_locked && <p>• 本標的當前價格鎖死於漲跌停板，撮合流動性受限。</p>}
              </div>
            </div>
          )}

          {/* Key Indicators Summary Card */}
          <div className="bg-[#0b0f17]/90 border border-[#182232] rounded-xl p-3.5 grid grid-cols-3 gap-2 text-xs font-mono">
            <div>
              <span className="text-[11px] text-sky-400/85">目前收盤價</span>
              <p className="text-base font-bold text-slate-100 tabular-nums mt-0.5">{stock.close.toFixed(2)} NTD</p>
            </div>
            <div>
              <span className="text-[11px] text-sky-400/85">主人與狗 (MA200乖離)</span>
              <p className="text-sm font-bold text-sky-300 tabular-nums mt-0.5">
                {stock.dog_ma200_bias !== null ? `+${(stock.dog_ma200_bias * 100).toFixed(1)}%` : '--'}
              </p>
            </div>
            <div>
              <span className="text-[11px] text-sky-400/85">60日量比 (VR60)</span>
              <p className="text-sm font-bold text-slate-100 tabular-nums mt-0.5">
                {stock.vr60 != null ? `${stock.vr60.toFixed(2)}x` : '--'}
              </p>
            </div>
          </div>

          {/* 2x2 = 5-1 Valuation Ladder Card */}
          <div className="bg-[#0b0f17]/90 border border-[#182232] rounded-xl p-3.5">
            <h4 className="text-xs font-bold text-slate-100 mb-2.5 font-mono flex items-center justify-between">
              <span>2×2 = 5-1 動態估值階梯 <span className="text-[10px] text-sky-400/85 font-normal">(Valuation Ladder)</span></span>
              <span className="text-[11px] text-sky-300 bg-sky-950/60 px-2 py-0.5 rounded border border-sky-500/30">
                相對位置: {((stock.ladder_position_pct ?? 0.5) * 100).toFixed(0)}%
              </span>
            </h4>

            <div className="space-y-1.5 text-xs font-mono">
              <div className="flex justify-between items-center p-2 rounded-lg bg-[#131a26]/70">
                <span className="text-amber-300 font-semibold">5: 狂熱高估目標價 (Euphoria)</span>
                <span className="text-slate-100 font-bold">{stock.ladder_5_euphoria ? stock.ladder_5_euphoria.toFixed(2) : (stock.close * 1.35).toFixed(2)} NTD</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg bg-[#131a26]/70">
                <span className="text-sky-300 font-semibold">4: MA200 均線基點 (主人 Base)</span>
                <span className="text-slate-100 font-bold">{stock.ladder_4_base ? stock.ladder_4_base.toFixed(2) : (stock.close / (1 + (stock.dog_ma200_bias || 0.1))).toFixed(2)} NTD</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded-lg bg-[#131a26]/70">
                <span className="text-emerald-300 font-semibold">-1: 拉回支撐低點 (Pullback)</span>
                <span className="text-slate-100 font-bold">{stock.ladder_minus1_pullback ? stock.ladder_minus1_pullback.toFixed(2) : (stock.close * 0.90).toFixed(2)} NTD</span>
              </div>
            </div>
          </div>

          {/* 4G 1% Risk Position Sizer Calculator */}
          <div className="bg-[#0b0f17]/90 border border-[#182232] rounded-xl p-3.5 space-y-3.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-xs font-bold text-slate-100">
                <Calculator className="w-4 h-4 text-[#38bdf8]" />
                <span>4G 1% 風控部位計算機</span>
              </div>
              <span className="text-[10px] font-mono text-sky-400/85">(1% Risk Sizer)</span>
            </div>

            {/* Portfolio Equity Input Section */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-[11px] text-sky-400/85 font-mono flex items-center space-x-1">
                  <span>投資組合總淨值</span>
                  <span className="text-slate-400">({equityUnit === 'wan' ? '萬元' : 'NTD 元'})</span>
                </label>
                {/* Unit Toggle */}
                <div className="flex items-center bg-[#131a26] border border-[#1e293b] rounded p-0.5 text-[10px] font-mono">
                  <button
                    type="button"
                    data-testid="sizer-unit-wan"
                    onClick={() => handleUnitSwitch('wan')}
                    className={`px-2 py-0.5 rounded transition-colors ${
                      equityUnit === 'wan'
                        ? 'bg-sky-500/25 text-[#38bdf8] font-bold'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    萬元
                  </button>
                  <button
                    type="button"
                    data-testid="sizer-unit-ntd"
                    onClick={() => handleUnitSwitch('ntd')}
                    className={`px-2 py-0.5 rounded transition-colors ${
                      equityUnit === 'ntd'
                        ? 'bg-sky-500/25 text-[#38bdf8] font-bold'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    元 (NTD)
                  </button>
                </div>
              </div>

              {/* Text Input without spinners */}
              <div className="relative">
                <input
                  data-testid="sizer-equity-input"
                  type="text"
                  inputMode="decimal"
                  value={equityInputStr}
                  onChange={(e) => handleEquityChange(e.target.value)}
                  className="w-full bg-[#131a26] border border-[#1e293b] rounded-lg px-2.5 py-1.5 font-mono text-white text-xs focus:outline-none focus:border-sky-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none pr-14"
                  placeholder={equityUnit === 'wan' ? '如: 80 或 200' : '如: 800000 或 2000000'}
                />
                <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] font-mono text-sky-400/70 pointer-events-none">
                  {equityUnit === 'wan' ? '萬元' : 'NTD'}
                </span>
              </div>

              {/* Quick Preset Buttons */}
              <div className="flex items-center space-x-1 pt-0.5 overflow-x-auto scrollbar-none">
                <span className="text-[10px] text-slate-400 font-mono shrink-0 mr-0.5">常用淨值:</span>
                {[
                  { label: '50萬', wan: 50, ntd: 500_000 },
                  { label: '80萬', wan: 80, ntd: 800_000 },
                  { label: '100萬', wan: 100, ntd: 1_000_000 },
                  { label: '200萬', wan: 200, ntd: 2_000_000 },
                  { label: '500萬', wan: 500, ntd: 5_000_000 },
                  { label: '1,000萬', wan: 1000, ntd: 10_000_000 },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    data-testid={`sizer-preset-${preset.wan}`}
                    onClick={() => handlePresetClick(preset.wan, preset.ntd)}
                    className={`px-2 py-0.5 rounded text-[10px] font-mono border transition-colors shrink-0 ${
                      portfolioEquity === preset.ntd
                        ? 'bg-sky-500/20 text-[#38bdf8] border-sky-500/40 font-bold'
                        : 'bg-[#131a26] hover:bg-[#1a2332] text-slate-300 border-[#1e293b]'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Formatted feedback preview */}
              <div className="text-[11px] font-mono text-slate-400 flex items-center justify-between px-0.5">
                <span>計算基準淨值:</span>
                <span className="text-sky-300 font-bold">
                  NT$ {portfolioEquity.toLocaleString()} ({formatWan(portfolioEquity)})
                </span>
              </div>
            </div>

            {/* Stop Loss Choice */}
            <div>
              <label className="text-[11px] text-sky-400/85 block mb-1 font-mono">停損基準選擇</label>
              <select
                data-testid="sizer-stopchoice-select"
                value={stopChoice}
                onChange={(e) => setStopChoice(e.target.value as StopChoice)}
                className="w-full bg-[#131a26] border border-[#1e293b] rounded-lg px-2.5 py-1.5 font-mono text-white text-xs focus:outline-none focus:border-sky-400"
              >
                <option value="atr">
                  2×ATR14 動態停損 ({roundToTwseTick(stock.close - 2.0 * (stock.atr14 ?? stock.close * 0.03), 'floor').toFixed(2)} NTD)
                </option>
                <option value="base_low">
                  20D 盤整前低停損 ({roundToTwseTick(stock.low_20d ?? stock.close * 0.92, 'floor').toFixed(2)} NTD)
                </option>
                <option value="manual">手動指定價格</option>
              </select>
            </div>

            {stopChoice === 'manual' && (
              <div className="p-2.5 rounded-lg bg-[#131a26]/70 border border-[#1e293b] space-y-1">
                <div className="flex justify-between items-center text-[11px] font-mono">
                  <label className="text-sky-400/85">自訂停損價格 (NTD)</label>
                  <span className="text-slate-400 text-[10px]">須低於市價 {stock.close.toFixed(2)}</span>
                </div>
                <input
                  data-testid="sizer-manual-stop-input"
                  type="text"
                  inputMode="decimal"
                  value={manualStopInputStr}
                  onChange={(e) => handleManualStopChange(e.target.value)}
                  className="w-full bg-[#0b0f17] border border-[#1e293b] rounded-lg px-2.5 py-1.5 font-mono text-white text-xs focus:outline-none focus:border-sky-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  placeholder={`例如: ${(stock.close * 0.95).toFixed(2)}`}
                />
              </div>
            )}

            {/* Discipline Multiplier Status & Simulation Toggle */}
            <div className="p-2.5 rounded-lg bg-[#131a26]/80 border border-[#1e293b] space-y-2 text-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1.5">
                  <span className="text-slate-300 font-mono text-[11px]">科斯托蘭尼乘數:</span>
                  <span className={`font-mono font-bold text-[11px] px-1.5 py-0.2 rounded border ${
                    effectiveMultiplier > 0
                      ? 'text-sky-300 bg-sky-950/60 border-sky-500/30'
                      : 'text-amber-400 bg-amber-950/60 border-amber-500/30'
                  }`}>
                    {effectiveMultiplier.toFixed(2)}x {simulationMode ? '(模擬試算)' : `(${stock.phase} 紀律)`}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="sizer-simulation-toggle"
                  onClick={() => setSimulationMode(!simulationMode)}
                  className={`px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-colors border ${
                    simulationMode
                      ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 hover:bg-amber-500/30'
                      : 'bg-sky-500/15 text-sky-300 border-sky-500/30 hover:bg-sky-500/25'
                  }`}
                >
                  {simulationMode ? '恢復真實紀律' : '開啟 1.0x 模擬試算'}
                </button>
              </div>

              {!simulationMode && disciplineMultiplier === 0 && (
                <div className="p-2 rounded bg-amber-950/30 border border-amber-500/30 text-[11px] text-amber-200/90 leading-relaxed">
                  <div className="flex items-center space-x-1 font-bold text-amber-300 mb-0.5">
                    <ShieldAlert className="w-3.5 h-3.5 shrink-0" />
                    <span>階段紀律約束：{stock.phase} 階段禁止買進</span>
                  </div>
                  <p className="text-[10px] text-slate-300">
                    依科斯托蘭尼蛋形理論，本階段屬於過熱或空頭週期，風控紀律乘數為 0.0x（建議部位為 0 股）。若欲觀察標準 1% 風險額度，請點擊上方按鈕開啟 1.0x 模擬試算。
                  </p>
                </div>
              )}
            </div>

            {/* Calculation Outputs Card */}
            <div className="p-3 bg-[#131a26]/90 rounded-xl border border-[#1e293b]/90 space-y-3">
              <div className="flex items-baseline justify-between border-b border-[#1e293b]/70 pb-2.5">
                <span className="text-xs font-semibold text-slate-200">建議下單部位：</span>
                <div className="text-right font-mono">
                  <span data-testid="sizer-lots" className="text-xl font-bold text-[#38bdf8]">{sizingResult.lots}</span>
                  <span className="text-xs text-slate-200 ml-1">張</span>
                  <span data-testid="sizer-odd-shares" className="text-base font-semibold text-sky-300 ml-2">+{sizingResult.odd_shares}</span>
                  <span className="text-xs text-slate-300 ml-1">零股</span>
                  <span data-testid="sizer-shares" className="text-xs text-slate-400 ml-2">({sizingResult.shares.toLocaleString()} 股)</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 p-2.5 rounded-lg bg-[#0b0f17]/85 text-[11px] font-mono text-slate-300">
                <div>
                  <span className="text-slate-400">停損觸發價:</span>
                  <strong data-testid="sizer-stop-price" className="text-slate-100 ml-1">
                    {sizingResult.stop_loss_price > 0 ? `${sizingResult.stop_loss_price.toFixed(2)} NTD` : '--'}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400">單股停損損失:</span>
                  <strong data-testid="sizer-pershare-loss" className="text-amber-300 ml-1">
                    {sizingResult.per_share_loss > 0 ? `NT$${sizingResult.per_share_loss.toFixed(2)}` : '--'}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400">1% 風險預算:</span>
                  <strong data-testid="sizer-risk-budget" className="text-sky-300 ml-1">
                    NT${Math.round(portfolioEquity * 0.01 * effectiveMultiplier).toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400">實際承擔風險:</span>
                  <strong data-testid="sizer-ntd-at-risk" className="text-rose-400 ml-1">
                    NT${sizingResult.ntd_at_risk.toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400">預估總成本:</span>
                  <strong data-testid="sizer-position-cost" className="text-slate-100 ml-1">
                    NT${sizingResult.position_cost.toLocaleString()}
                  </strong>
                </div>
                <div>
                  <span className="text-slate-400">部位佔淨值比:</span>
                  <strong data-testid="sizer-position-weight" className="text-sky-300 ml-1">
                    {(sizingResult.position_weight * 100).toFixed(1)}%
                  </strong>
                </div>
              </div>

              {/* Zero-share live diagnostic card if shares === 0 */}
              {sizingResult.shares === 0 && portfolioEquity > 0 && (
                <div className="p-2.5 rounded-lg bg-amber-950/30 border border-amber-500/30 text-[11px] text-amber-200 space-y-1">
                  <div className="flex items-center space-x-1.5 font-bold text-amber-300">
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                    <span>部位試算結果為 0 股之原因分析：</span>
                  </div>
                  {effectiveMultiplier === 0 ? (
                    <p className="text-slate-300 text-[10px]">
                      目前處於 {stock.phase} 階段，科斯托蘭尼風控紀律乘數為 0.0x（禁止買進建立新多單）。請點擊上方按鈕開啟「1.0x 模擬試算」。
                    </p>
                  ) : (
                    <p className="text-slate-300 text-[10px] leading-relaxed">
                      當前 1% 風險預算為 <strong>NT$ {Math.round(portfolioEquity * 0.01 * effectiveMultiplier).toLocaleString()}</strong>，而本標的（市價 {stock.close.toFixed(2)} / 停損價 {sizingResult.stop_loss_price.toFixed(2)}）每股停損損失為 <strong>NT$ {sizingResult.per_share_loss.toFixed(1)}</strong>。<br />
                      因單股停損風險大於總風險預算（{Math.round(portfolioEquity * 0.01 * effectiveMultiplier)} &lt; {sizingResult.per_share_loss.toFixed(1)}），依 1% 風控原則不足以購買 1 股。請調高投資組合淨值或縮小停損差距。
                    </p>
                  )}
                </div>
              )}

              {/* Binding Constraint explanation */}
              <div className="p-2 rounded-lg bg-[#0b0f17]/60 border border-[#1e293b]/60 text-[11px] space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sky-400/85 font-mono text-[10px]">拘束限制 (Binding Constraint):</span>
                  <span
                    data-testid="sizer-constraint"
                    className={`font-mono font-bold text-[10px] px-2 py-0.5 rounded border ${constraintInfo.color}`}
                  >
                    {sizingResult.constraint}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 font-sans">
                  {constraintInfo.desc}
                </p>
              </div>
            </div>

            {/* Warning or Rejection message if any */}
            {sizingResult.rejection_reason && (
              <div className="p-2.5 rounded-lg bg-amber-950/40 border border-amber-500/40 flex items-center space-x-1.5 text-[11px] text-amber-300">
                <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                <span>{sizingResult.rejection_reason}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
